<template>
  <div class="voyage-detail-wrapper">
    <div class="voyage-detail-sidebar bg-white">
      <div v-if="isLoading" class="loading-state">
        <wave-loader>Loading voyage details...</wave-loader>
      </div>

      <div v-else-if="!voyage" class="not-found-state">
        <p class="text-secondary">Voyage not found.</p>
      </div>

      <div v-else class="voyage-sidebar-content p-2">
        <div class="voyage-header">
          <b-button variant="primary" size="sm" class="voyage-back" @click="goBack">
            <i class="ri-arrow-left-line"></i> Back to Voyages
          </b-button>
          <h5 class="voyage-title">{{ voyage.mvygName }}</h5>
          <b-button variant="outline-primary" size="sm" class="voyage-compare-link" @click="goCompare">
            <i class="ri-arrow-left-right-line"></i> Compare
          </b-button>
        </div>

        <VoyageSectionNav :sections="navSections" />

        <div id="voyage-info" class="voyage-top">
          <VoyageTrackMap :voyage="voyage" />
          <div class="voyage-top-side">
            <VoyageInfoPanel :voyage="voyage" />
            <VoyageKpiGrid
              :kpis="kpis"
              :duration="formatVoyageDuration(voyage.mvygStartTime, voyage.mvygEndTime)"
            />
          </div>
        </div>

        <div v-if="visiblePanels.length" class="voyage-panel-grid">
          <component
            :is="panel.component"
            v-for="(panel, index) in visiblePanels"
            :key="panel.id"
            :anchor-id="panel.id"
            :model="panel.model"
            :badges="panel.badges"
            :class="{ 'voyage-panel-wide': isLoneLast(index) }"
          />
        </div>
        <p v-else class="small text-secondary voyage-panel-empty">No engine or weather data for this voyage.</p>

        <div id="voyage-charts" class="voyage-charts">
          <PerformanceCharts
            :voyage="voyage"
            :rpm-data="rpmData"
            :flowmeter-data="flowmeterData"
            :gps-data="gpsData"
            :loading="isChartsLoading"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useVoyageData } from '@/views/voyage/composables/useVoyageData';
import { useVoyageFormatters } from '@/views/voyage/composables/useVoyageFormatters';
import {
  voyageDurationSeconds,
  buildKpis,
  buildMainEngine,
  buildAuxEngine,
  buildWeather,
  buildOperationMode,
  buildEfficiency,
  buildCoverageBadges,
} from '@/views/voyage/utils/voyageSummaryModel';
import VoyageInfoPanel from '@/views/voyage/components/VoyageInfoPanel.vue';
import VoyageTrackMap from '@/views/voyage/components/VoyageTrackMap.vue';
import VoyageSectionNav from '@/views/voyage/components/VoyageSectionNav.vue';
import VoyageKpiGrid from '@/views/voyage/components/summary/VoyageKpiGrid.vue';
import MainEnginePanel from '@/views/voyage/components/summary/MainEnginePanel.vue';
import AuxEnginePanel from '@/views/voyage/components/summary/AuxEnginePanel.vue';
import EfficiencyPanel from '@/views/voyage/components/summary/EfficiencyPanel.vue';
import WeatherWavePanel from '@/views/voyage/components/summary/WeatherWavePanel.vue';
import OperationModePanel from '@/views/voyage/components/summary/OperationModePanel.vue';
import PerformanceCharts from '@/views/voyage/components/PerformanceCharts.vue';

// Store reference to composable functions
let fetchTelemetryDataFromComposable;

export default {
  name: 'VoyageDetail',
  components: {
    VoyageInfoPanel,
    VoyageTrackMap,
    VoyageSectionNav,
    VoyageKpiGrid,
    PerformanceCharts,
  },
  setup() {
    const {
      loadAllVoyageData,
      loadTelemetryData,
    } = useVoyageData();
    const { formatVoyageDuration } = useVoyageFormatters();

    // Store reference to the composable function
    fetchTelemetryDataFromComposable = loadTelemetryData;

    return {
      loadAllVoyageData,
      formatVoyageDuration,
    };
  },
  data() {
    return {
      voyage: null,
      summary: null,
      gpsData: null,
      rpmData: null,
      flowmeterData: null,
      isLoading: false,
      isChartsLoading: true,
    };
  },
  computed: {
    kpis() {
      return buildKpis(this.summary);
    },
    // urutan = urutan grid: ME | AE, Efficiency | Weather, Op Mode
    visiblePanels() {
      const coverage = this.summary?.coverage;
      return [
        {
          id: 'voyage-main-engine',
          label: 'Main Engine',
          component: MainEnginePanel,
          model: buildMainEngine(this.summary),
          badges: buildCoverageBadges(coverage, ['gps', 'rpm', 'flowmeter']),
        },
        {
          id: 'voyage-aux-engine',
          label: 'Auxiliary',
          component: AuxEnginePanel,
          model: buildAuxEngine(this.summary),
          badges: buildCoverageBadges(coverage, ['ae']),
        },
        {
          id: 'voyage-efficiency',
          label: 'Efficiency',
          component: EfficiencyPanel,
          model: buildEfficiency(this.summary, voyageDurationSeconds(this.voyage)),
          badges: buildCoverageBadges(coverage, ['flowmeter', 'gps']),
        },
        {
          id: 'voyage-weather',
          label: 'Weather',
          component: WeatherWavePanel,
          model: buildWeather(this.summary),
          badges: buildCoverageBadges(coverage, ['gps']),
        },
        {
          id: 'voyage-operation-mode',
          label: 'Op. Mode',
          component: OperationModePanel,
          model: buildOperationMode(this.summary),
          badges: buildCoverageBadges(coverage, ['operationMode']),
        },
      ].filter((panel) => panel.model.hasData);
    },
    navSections() {
      return [
        { id: 'voyage-info', label: 'Info' },
        ...this.visiblePanels.map(({ id, label }) => ({ id, label })),
        { id: 'voyage-charts', label: 'Charts' },
      ];
    },
  },
  async mounted() {
    await this.loadVoyageData();
  },
  methods: {
    async loadVoyageData() {
      try {
        this.isLoading = true;
        this.isChartsLoading = true;

        const { voyage, summary } = await this.loadAllVoyageData({
          voyageId: this.$route.params.id,
        });

        this.voyage = voyage;
        this.summary = summary;

        if (!voyage) {
          this.$toast.error('Voyage not found');
          this.isLoading = false;
          this.isChartsLoading = false;
          return false;
        }

        // Voyage master + summary loaded - show the data immediately
        this.isLoading = false;

        // Load telemetry data (GPS, RPM, Flowmeter)
        await this._fetchTelemetryData();
        return true;
      } catch (error) {
        if (axios.isCancel(error)) return false;
        console.error('loadVoyageData error:', error?.message);
        this.$toast.error(error.message || 'Failed to load voyage details');
        this.isLoading = false;
        this.isChartsLoading = false;
        return false;
      }
    },
    async _fetchTelemetryData() {
      try {
        if (!this.voyage) {
          console.warn('_fetchTelemetryData: voyage is not available');
          return;
        }

        const {
          gpsData, rpmData, flowmeterData,
        } = await fetchTelemetryDataFromComposable(this.voyage);

        this.gpsData = gpsData;
        this.rpmData = rpmData;
        this.flowmeterData = flowmeterData;

        this.isChartsLoading = false;
      } catch (error) {
        console.error('_fetchTelemetryData error:', error?.message);
        this.isChartsLoading = false;
      }
    },
    goBack() {
      const query = this.voyage?.mvygMassId ? { asset: this.voyage.mvygMassId } : {};
      this.$router.push({ name: 'Voyage', query });
    },
    goCompare() {
      this.$router.push({ name: 'VoyageCompare', query: { a: String(this.voyage.mvygId) } });
    },
    // panel ganjil terakhir dilebarin biar gak ada lubang
    isLoneLast(index) {
      const count = this.visiblePanels.length;
      return count % 2 === 1 && index === count - 1;
    },
  },
};
</script>

<style scoped>
.voyage-detail-wrapper {
  width: 100%;
}

.voyage-detail-sidebar {
  width: 100%;
}

.voyage-detail-sidebar::-webkit-scrollbar {
  display: none;
}

.voyage-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 12px;
  margin-bottom: 8px;
}

.voyage-title {
  font-size: 16.8px;
  font-weight: 600;
  color: var(--mz-text);
  margin: 0;
}

.voyage-compare-link {
  margin-left: auto;
}

.voyage-top {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;
  margin-bottom: 10px;
  scroll-margin-top: 124px;
}

.voyage-top-side {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.voyage-panel-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.voyage-panel-wide {
  grid-column: 1 / -1;
}

.voyage-panel-empty {
  margin: 0 0 10px;
}

.voyage-charts {
  scroll-margin-top: 124px;
}

@media (max-width: 991.98px) {
  .voyage-top {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {
  .voyage-panel-grid {
    grid-template-columns: 1fr;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 300px;
  gap: 1rem;
}

.not-found-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}
</style>
